import { useWindowSize } from "hooks/useWindowSize";
import Loader from "components/DesignSystem/Loader/loader";
import Async from "components/DesignSystem/AsyncComponents/Async";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { PriceInput } from "components/PriceInput/PriceInput";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { formatDateByViewType } from "utils/formatDate";
import { YYYY_MM_DD } from "constants/date";
import React, { ChangeEvent, ComponentProps, useMemo, useState } from "react";
import { debounce } from "utils/debouncing";
import { UpdateFilterFunction } from "hooks/useFilter";
import {
  DateRangeValue,
  getDateRange,
  getDateRangeWithSetting,
} from "utils/getDateRange";
import { useGetStartingDateQuery } from "store/apis/chartOfAccounts";
import { PERIOD } from "constants/revenueMetrics";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { usePaginatedQuery } from "hooks/usePaginatedQuery";
import { useGetVendorStatesQuery } from "store/apis/vendorBills";
import { DateFilter } from "components/Filters/DateFilter";
import { BooksDateFilter } from "components/Filters/BooksDateFilter";
import {
  BILL_STATUS,
  BILL_STATUS_OPTIONS,
  BILL_STATUS_GROUP_TYPES,
  BILL_STATUS_GROUP_TYPES_TYPE,
  BILL_STATUS_GROUP_DATA_MAP,
  BILLS_SOURCES,
  FINANCIAL_RECORD_TYPE,
} from "constants/vendorBills";
import { useLazyGetAllTransactionMerchantsQuery } from "store/apis/merchant";
import { useUpdateQuery } from "hooks/useQuery";
import { Form, Formik } from "formik";
import { DUE_DATE_PERIOD } from "components/InvoiceFilter/InvoiceFilter";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { Button } from "components/DesignSystem/Button/Button";
import { getDatePeriod } from "utils/getDatePeriod";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { Merchant } from "types/Models/merchant";

export type VendorBillsFilterType = {
  BILL_START_DATE: string;
  BILL_END_DATE: string;
  DUEDATE_START_DATE: string;
  DUEDATE_END_DATE: string;
  BILL_DATE: string;
  DUE_DATE: string;
  SOURCE: string[];
  STATUS: (keyof typeof BILL_STATUS)[];
  STATUS_GROUP: BILL_STATUS_GROUP_TYPES_TYPE | null;
  VENDOR: string;
  VENDOR_STATE: string[];
  MIN_AMOUNT: string | "";
  MAX_AMOUNT: string | "";
  SEARCH: string | null;
  BILL_TYPE: keyof typeof FINANCIAL_RECORD_TYPE;
  PAGE_NUM: number;
};

const Amount = ({
  filter,
  updateFilter,
}: {
  filter: VendorBillsFilterType;
  updateFilter: UpdateFilterFunction<VendorBillsFilterType>;
}) => {
  const { update } = useUpdateQuery();
  const minAmount = filter.MIN_AMOUNT;
  const maxAmount = filter.MAX_AMOUNT;

  const handleAmountChange = debounce((e: ChangeEvent<HTMLFormElement>) => {
    let { value, name } = e.target;
    const numericValue = value.replace(/[^\d.-]/g, "");
    update({ query: name, value: numericValue });

    if (name === "minAmount") {
      updateFilter("MIN_AMOUNT", numericValue);
    } else if (name === "maxAmount") {
      updateFilter("MAX_AMOUNT", numericValue);
    }
  });

  return (
    <Formik
      initialValues={{
        minAmount: minAmount || "",
        maxAmount: maxAmount || "",
      }}
      onSubmit={() => {}}
    >
      {({ setValues }) => {
        return (
          <div className="t-flex t-flex-col t-gap-5">
            <div className="t-flex t-flex-col t-gap-3">
              <Form
                className="all:unset t-flex t-flex-col t-gap-4"
                onChange={handleAmountChange}
              >
                <PriceInput
                  name="minAmount"
                  placeholder="0.00"
                  label="Min. Amount"
                />
                <PriceInput
                  name="maxAmount"
                  placeholder="0.00"
                  label="Max. Amount"
                />
              </Form>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

const statusGroupFilter = ({
  filter,
  updateFilter,
  selectedStatusGroup,
  selectedStatus,
}: {
  filter: VendorBillsFilterType;
  updateFilter: UpdateFilterFunction<VendorBillsFilterType>;
  selectedStatusGroup?: BILL_STATUS_GROUP_TYPES_TYPE;
  selectedStatus?: (keyof typeof BILL_STATUS)[];
}) => {
  if (selectedStatusGroup) {
    updateFilter("STATUS_GROUP", selectedStatusGroup);
    updateFilter(
      "STATUS",
      BILL_STATUS_GROUP_DATA_MAP[selectedStatusGroup].statuses
    );
    return;
  }

  if (selectedStatus) {
    updateFilter("STATUS", selectedStatus);
    if (selectedStatus.length > 0) {
      updateFilter("STATUS_GROUP", BILL_STATUS_GROUP_TYPES.ACTIVE);
    }
  }
};

const StatusFilter = ({
  filter,
  updateFilter,
}: {
  filter: VendorBillsFilterType;
  updateFilter: UpdateFilterFunction<VendorBillsFilterType>;
}) => {
  const selectedStatus = filter.STATUS || [];

  return (
    <span className="t-flex t-flex-col ">
      <div className="t-self-end">
        <Button
          onClick={() => {
            updateFilter("STATUS_GROUP", null);
            updateFilter("STATUS", []);
          }}
          customType="text"
          size="xsmall"
        >
          <span>Reset</span>
        </Button>
      </div>
      <RadioGroup.Root
        value={filter.STATUS_GROUP ?? ""}
        onValueChange={(v: BILL_STATUS_GROUP_TYPES_TYPE) => {
          statusGroupFilter({
            filter,
            updateFilter,
            selectedStatusGroup: v,
          });
        }}
      >
        <RadioGroup.Content className="t-flex t-flex-col">
          <RadioGroup.Item
            align="start"
            value={BILL_STATUS_GROUP_TYPES.ACTIVE}
            className="!t-h-auto"
            checked={
              filter.STATUS_GROUP === BILL_STATUS_GROUP_TYPES.ACTIVE &&
              selectedStatus.length > 0
            }
          >
            <p>Active</p>
            <span className=" t-flex t-flex-col t-gap-2">
              {BILL_STATUS_OPTIONS.map(({ value, label }) => {
                return (
                  <Checkbox
                    key={value}
                    label={<>{label}</>}
                    name={value}
                    checked={selectedStatus.includes(value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        statusGroupFilter({
                          filter,
                          updateFilter,
                          selectedStatus: [...selectedStatus, value],
                        });
                      } else {
                        statusGroupFilter({
                          filter,
                          updateFilter,
                          selectedStatus: selectedStatus.filter(
                            (s) => s !== value
                          ),
                        });
                      }
                    }}
                  />
                );
              })}
            </span>
          </RadioGroup.Item>
          <RadioGroup.Item
            align="start"
            value={BILL_STATUS_GROUP_TYPES.DRAFT}
            className="!t-h-auto t-mt-4"
            checked={filter.STATUS_GROUP === BILL_STATUS_GROUP_TYPES.DRAFT}
          >
            <p>Draft</p>
          </RadioGroup.Item>
        </RadioGroup.Content>
      </RadioGroup.Root>
    </span>
  );
};
const SourceFilter = ({
  filter,
  updateFilter,
}: {
  filter: VendorBillsFilterType;
  updateFilter: UpdateFilterFunction<VendorBillsFilterType>;
}) => {
  const sourceList = [
    BILLS_SOURCES.MANUAL,
    BILLS_SOURCES.BILL_COM,
    BILLS_SOURCES.FORWARDED_VIA_EMAIL,
  ];

  const selectedSource = filter.SOURCE || [];

  const onHandleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;

    if (Array.isArray(selectedSource)) {
      let newSource: Set<string> = new Set(selectedSource);
      if (checked) {
        newSource = new Set([...selectedSource, value]);
      } else {
        newSource = new Set(
          [...selectedSource]?.filter((merchant) => merchant !== value)
        );
      }
      updateFilter("SOURCE", [...newSource].length > 0 ? [...newSource] : []);
    }
  };

  return (
    <MultiSelectFilter
      options={sourceList.map((source) => ({
        value: source.value,
        label: source.label,
      }))}
      selected={selectedSource}
      onChange={(newValues) => updateFilter("SOURCE", newValues)}
      isSearchable={false}
      isSelectAll={false}
    />
  );
};

function getInitialValues(filter: any, filterType: FilterType) {
  if (filterType === FilterType.BILL) {
    return {
      BILL_START_DATE: "",
      BILL_END_DATE: "",
    };
  }

  if (filterType === FilterType.DUE_DATE) {
    return {
      DUEDATE_START_DATE: "",
      DUEDATE_END_DATE: "",
    };
  }

  return {};
}

const BillDateFilter = ({
  filterType,
  filter,
  updateFilter,
}: {
  filterType: FilterType;
  filter: VendorBillsFilterType;
  updateFilter: UpdateFilterFunction<VendorBillsFilterType>;
}) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );
  const { data: startDateData } = useGetStartingDateQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );
  const bookkeepingStartDate = startDateData?.start_date;

  const onDateChange: ComponentProps<typeof BooksDateFilter>["updateFilter"] = (
    name,
    value
  ) => {
    if (!value) return;

    if (name === "SELECT_PERIOD" && value !== "CUSTOM") {
      const { startDate, endDate } = getDateRangeWithSetting(
        value as DateRangeValue,
        bookkeepingSettings?.tax_year_end,
        bookkeepingStartDate
      );
      const startDateObj = startDate.format(YYYY_MM_DD);
      const endDateObj = endDate.format(YYYY_MM_DD);
      updateFilter("BILL_START_DATE", startDateObj);
      updateFilter("BILL_END_DATE", endDateObj);
      return;
    }

    if (name === "START_DATE") updateFilter("BILL_START_DATE", value);
    if (name === "END_DATE") updateFilter("BILL_END_DATE", value);
  };

  const values = {
    SELECT_PERIOD: "",
    START_DATE: filter.BILL_START_DATE || "",
    END_DATE: filter.BILL_END_DATE || "",
  };

  return (
    <div className="t-flex t-flex-col t-gap-3 t-w-full">
      <BooksDateFilter
        datePeriod={
          filterType === FilterType.DUE_DATE ? DUE_DATE_PERIOD : getDatePeriod
        }
        maxEndDate={undefined}
        skipDateUpdateOnPeriodChange
        pickerType="date"
        values={values}
        updateFilter={onDateChange}
      />
    </div>
  );
};

const DueDateFilter = ({
  filterType,
  filter,
  updateFilter,
}: {
  filterType: FilterType;
  filter: VendorBillsFilterType;
  updateFilter: UpdateFilterFunction<VendorBillsFilterType>;
}) => {
  const onDateChange: ComponentProps<typeof DateFilter>["updateFilter"] = (
    name,
    value
  ) => {
    if (!value) return;

    if (name === "SELECT_PERIOD" && value !== "CUSTOM") {
      const { startDate, endDate } = getDateRange(value as DateRangeValue);
      const startDateObj = startDate.format(YYYY_MM_DD);
      const endDateObj = endDate.format(YYYY_MM_DD);

      updateFilter("DUEDATE_START_DATE", startDateObj);
      updateFilter("DUEDATE_END_DATE", endDateObj);
      return;
    }

    if (name === "START_DATE") updateFilter("DUEDATE_START_DATE", value);
    if (name === "END_DATE") updateFilter("DUEDATE_END_DATE", value);
  };

  const values = {
    SELECT_PERIOD: "",
    START_DATE: filter.DUEDATE_START_DATE || "",
    END_DATE: filter.DUEDATE_END_DATE || "",
  };

  return (
    <div className="t-flex t-flex-col t-gap-3 t-w-full">
      <DateFilter
        datePeriod={
          filterType === FilterType.DUE_DATE ? DUE_DATE_PERIOD : undefined
        }
        maxEndDate={undefined}
        skipDateUpdateOnPeriodChange
        pickerType="date"
        values={values}
        updateFilter={onDateChange}
      />
    </div>
  );
};

enum FilterType {
  BILL = "BILL",
  DUE_DATE = "DUE_DATE",
}

const InterDateFilter = ({
  filterType,
  filter,
  updateFilter,
}: {
  filterType: FilterType;
  filter: VendorBillsFilterType;
  updateFilter: UpdateFilterFunction<VendorBillsFilterType>;
}) => {
  return (
    <BillDateFilter
      filterType={filterType}
      filter={filter}
      updateFilter={updateFilter}
    />
  );
};

const VendorFilter = ({
  updateFilter,
  filterValues,
}: {
  updateFilter: UpdateFilterFunction<VendorBillsFilterType>;
  filterValues: VendorBillsFilterType;
}) => {
  const group = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [vendorSearchValue, setVendorSearchValue] = useState("");

  const {
    data: vendorData,
    loadNext,
    isLoading,
    isSuccess,
  } = usePaginatedQuery<{
    merchants?: Merchant[];
  }>(useLazyGetAllTransactionMerchantsQuery, "merchants", {
    groupId: group.uuid,
    entityId: entityId,
    search_term: vendorSearchValue,
  });

  const updateFilterCallback =
    (name: Parameters<typeof updateFilter>[0]) =>
    (newValues: string[] | string) => {
      if (name === "VENDOR" && Array.isArray(newValues)) {
        updateFilter(name, newValues.join(","));
      } else {
        updateFilter(name, newValues);
      }
    };

  const onVendorSearch = debounce((value) => setVendorSearchValue(value));

  if (isLoading && !isSuccess) {
    return (
      <div className="t-w-full t-h-full t-flex t-justify-center t-align-middle">
        <Loader />
      </div>
    );
  }

  if (vendorData?.merchants) {
    let selectedVendors: string[] = [];
    if (
      typeof filterValues.VENDOR === "string" &&
      filterValues.VENDOR.length > 0
    ) {
      selectedVendors = filterValues.VENDOR.split(",");
    } else if (Array.isArray(filterValues.VENDOR)) {
      selectedVendors = filterValues.VENDOR;
    }
    return (
      <MultiSelectFilter
        onSearchValueChange={onVendorSearch}
        hasMore={true}
        totalCount={vendorData?.merchants.length}
        loadNext={loadNext}
        onChange={updateFilterCallback("VENDOR")}
        options={vendorData?.merchants.map(({ uuid, name }) => ({
          value: uuid,
          label: name,
        }))}
        selected={selectedVendors}
      />
    );
  }

  return null;
};

const StateFilter = ({
  filterValues,
  updateFilter,
}: {
  filterValues: VendorBillsFilterType;
  updateFilter: UpdateFilterFunction<VendorBillsFilterType>;
}) => {
  const group = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { data, isLoading, isSuccess, isError } = useGetVendorStatesQuery({
    groupId: group.uuid,
    entityId,
  });

  const updateFilterCallback = (newValues: string[]) => {
    updateFilter("VENDOR_STATE", newValues);
  };

  const stateOptions = (data?.states || []).map((state) => ({
    value: state,
    label: state,
  }));

  return (
    <Async.Root
      isLoading={isLoading}
      isSuccess={isSuccess}
      isError={isError}
      isEmpty={stateOptions.length === 0}
    >
      <Async.Empty>
        <MultiSelectFilter
          options={stateOptions}
          selected={filterValues.VENDOR_STATE || []}
          onChange={updateFilterCallback}
          isSelectAll={false}
        />
      </Async.Empty>
      <Async.Success>
        <MultiSelectFilter
          options={stateOptions}
          selected={filterValues.VENDOR_STATE || []}
          onChange={updateFilterCallback}
        />
      </Async.Success>
    </Async.Root>
  );
};

const formatDateRangeLabel = (startDate: string, endDate: string) => {
  if (startDate && endDate) {
    return `${formatDateByViewType(
      startDate,
      PERIOD.TOTALS
    )} - ${formatDateByViewType(endDate, PERIOD.TOTALS)}`;
  }
  if (startDate) {
    return `Since ${formatDateByViewType(startDate, PERIOD.TOTALS)}`;
  }
  return `Till ${formatDateByViewType(endDate, PERIOD.TOTALS)}`;
};

export const VendorBillsFilter = ({
  filterValues,
  updateFilter,
}: {
  filterValues: VendorBillsFilterType;
  updateFilter: UpdateFilterFunction<VendorBillsFilterType>;
}) => {
  const appliedFilterCount = useMemo(
    () =>
      Object.keys(filterValues).reduce((acc, key) => {
        if (key === "BILL_TYPE" || key === "PAGE_NUM" || key === "STATUS_GROUP")
          return acc;
        if (key === "STATUS" || key === "SOURCE" || key === "VENDOR_STATE") {
          return (
            acc +
            (Array.isArray(filterValues[key]) ? filterValues[key].length : 0)
          );
        }
        if (filterValues[key as keyof VendorBillsFilterType]) {
          return acc + 1;
        }
        return acc;
      }, 0),
    [filterValues]
  );

  const { isMobile } = useWindowSize();

  return (
    <div className="t-flex t-justify-between t-items-center t-bg-surface">
      <div className="t-flex t-gap-2 t-items-center">
        <Filter.Root
          defaultValue="vendor"
          hideDivider={isMobile}
          title={
            appliedFilterCount > 0 ? (
              <span className="t-text-text-30">({appliedFilterCount})</span>
            ) : null
          }
          capsule={
            <span className="t-contents sm:t-hidden">
              <Chip
                onClick={() => {
                  statusGroupFilter({
                    filter: filterValues,
                    updateFilter,
                    selectedStatus: [BILL_STATUS.OVERDUE],
                  });
                }}
                onRemove={() => {
                  updateFilter(
                    "STATUS",
                    filterValues.STATUS?.filter(
                      (sts) => sts !== BILL_STATUS.OVERDUE
                    ) || []
                  );
                  updateFilter("STATUS_GROUP", null);
                }}
                isRemovable={true}
                isFixedFilter={true}
                isActive={filterValues.STATUS?.includes(BILL_STATUS.OVERDUE)}
                filterType="status"
              >
                Overdue
              </Chip>
              <Chip
                onClick={() => {
                  statusGroupFilter({
                    filter: filterValues,
                    updateFilter,
                    selectedStatusGroup: BILL_STATUS_GROUP_TYPES.DRAFT,
                  });
                }}
                onRemove={() => {
                  updateFilter("STATUS_GROUP", null);
                }}
                isRemovable={true}
                isFixedFilter={true}
                isActive={
                  filterValues.STATUS_GROUP === BILL_STATUS_GROUP_TYPES.DRAFT
                }
                filterType="status"
              >
                Draft
              </Chip>
              {(filterValues.BILL_START_DATE || filterValues.BILL_END_DATE) && (
                <Chip
                  filterType="billDate"
                  onRemove={() => {
                    updateFilter("BILL_START_DATE", "");
                    updateFilter("BILL_END_DATE", "");
                  }}
                  isRemovable={true}
                  isActive
                >
                  {"Bill Date: "}
                  {formatDateRangeLabel(
                    filterValues.BILL_START_DATE,
                    filterValues.BILL_END_DATE
                  )}
                </Chip>
              )}
              {(filterValues.DUEDATE_START_DATE ||
                filterValues.DUEDATE_END_DATE) && (
                <Chip
                  onRemove={() => {
                    updateFilter("DUEDATE_START_DATE", "");
                    updateFilter("DUEDATE_END_DATE", "");
                  }}
                  isRemovable={true}
                  isActive
                  filterType="dueDate"
                >
                  {"Due Date: "}
                  {formatDateRangeLabel(
                    filterValues.DUEDATE_START_DATE,
                    filterValues.DUEDATE_END_DATE
                  )}
                </Chip>
              )}
              {filterValues.STATUS && filterValues.STATUS.length > 0 && (
                <Chip
                  key={"status"}
                  onRemove={() => {
                    updateFilter("STATUS_GROUP", null);
                    statusGroupFilter({
                      filter: filterValues,
                      updateFilter,
                      selectedStatus: [],
                    });
                  }}
                  isRemovable={true}
                  isActive
                  filterType="status"
                >
                  {`Active (${filterValues.STATUS.length})`}
                </Chip>
              )}
              {filterValues.SOURCE &&
                filterValues.SOURCE &&
                filterValues.SOURCE.length > 0 && (
                  <Chip
                    onRemove={() => {
                      updateFilter("SOURCE", []);
                    }}
                    isRemovable={true}
                    isActive
                    filterType="source"
                  >
                    {`Sources (${filterValues.SOURCE.length})`}
                  </Chip>
                )}
              {filterValues.MIN_AMOUNT &&
                filterValues.MIN_AMOUNT.length > 0 && (
                  <Chip
                    onRemove={() => {
                      updateFilter("MIN_AMOUNT", "");
                    }}
                    isRemovable={true}
                    isActive
                    filterType="amount"
                  >
                    Min Amount ${filterValues.MIN_AMOUNT}
                  </Chip>
                )}
              {filterValues.MAX_AMOUNT &&
                filterValues.MAX_AMOUNT.length > 0 && (
                  <Chip
                    onRemove={() => {
                      updateFilter("MAX_AMOUNT", "");
                    }}
                    isRemovable={true}
                    isActive
                    filterType="amount"
                  >
                    Max Amount ${filterValues.MAX_AMOUNT}
                  </Chip>
                )}
              {typeof filterValues.VENDOR === "string" &&
                filterValues.VENDOR.length > 0 && (
                  <Chip
                    onRemove={() => {
                      updateFilter("VENDOR", "");
                    }}
                    isRemovable={true}
                    isActive
                    filterType="vendor"
                  >
                    Vendor ({filterValues.VENDOR.split(",").length})
                  </Chip>
                )}
              {filterValues.VENDOR_STATE.length > 0 && (
                <Chip
                  onRemove={() => {
                    updateFilter("VENDOR_STATE", []);
                  }}
                  isRemovable={true}
                  isActive
                  filterType="vendorState"
                >
                  Vendor State ({filterValues.VENDOR_STATE.length})
                </Chip>
              )}
            </span>
          }
        >
          <Filter.Portal>
            <Filter.List>
              <Filter.ListItem value="source">Source</Filter.ListItem>
              <Filter.ListItem value="vendor">Vendor</Filter.ListItem>
              <Filter.ListItem value="vendorState">
                Vendor State
              </Filter.ListItem>
              <Filter.ListItem value="amount">Amount</Filter.ListItem>
              <Filter.ListItem value="billDate">Bill Date</Filter.ListItem>
              <Filter.ListItem value="dueDate">Due Date</Filter.ListItem>
              <Filter.ListItem value="status">Status</Filter.ListItem>
            </Filter.List>
            <Filter.Body value="amount" block>
              <Amount filter={filterValues} updateFilter={updateFilter} />
            </Filter.Body>
            <Filter.Body value="billDate" block>
              <BillDateFilter
                filterType={FilterType.BILL}
                filter={filterValues}
                updateFilter={updateFilter}
              />
            </Filter.Body>
            <Filter.Body value="dueDate" block>
              <DueDateFilter
                filterType={FilterType.DUE_DATE}
                filter={filterValues}
                updateFilter={updateFilter}
              />
            </Filter.Body>
            <Filter.Body value="source">
              <SourceFilter filter={filterValues} updateFilter={updateFilter} />
            </Filter.Body>
            <Filter.Body value="vendor" block>
              <VendorFilter
                updateFilter={updateFilter}
                filterValues={filterValues}
              />
            </Filter.Body>
            <Filter.Body value="vendorState" block>
              <StateFilter
                updateFilter={updateFilter}
                filterValues={filterValues}
              />
            </Filter.Body>
            <Filter.Body value="status" block>
              <StatusFilter updateFilter={updateFilter} filter={filterValues} />
            </Filter.Body>
          </Filter.Portal>
        </Filter.Root>
      </div>
    </div>
  );
};
